import { FolderSettingUpdateDto } from '@common/dto/folder-settings';
import { DatasheetFolderListDto } from '@common/dto/folder/datasheet-list-dto';
import { FolderGetDto } from '@common/dto/folder/get-dto';
import { FolderListDto } from '@common/dto/folder/list-dto';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { HttpSuccessDto } from '@common/dto/http';
import client, { clientTransformed } from '@common/utils/rest';

const URL_PREFIX = 'api/folders';

export const getFolders = async (params: { context: number }) => {
  return client({
    path: URL_PREFIX,
    method: 'GET',
    params,
  }).then(
    (response: { entity: Array<FolderListDto> }) => {
      return response.entity;
    },
  );
};

export const getPedigreeFolder = (folderId: FolderGetDto['id']) => {
	return client({
    path: 'api/pedigree/getPedigreeFolder',
    method: 'GET',
		params: {
      folderId
    },
  }).then((response: HttpSuccessDto<FolderGetDto>) => {
    return response.entity;
  });
};

export const savePedigreeFolder = (folderDto: Partial<FolderSettingUpdateDto>) => {
	return client({
    path: 'api/pedigree/savePedigreeFolder',
    method: 'POST',
		entity: folderDto,
  });
};

export const deletePedigreeFolder = (folderId: FolderGetDto['id']) => {
  return client({
    path: 'api/folders/deletePedigreeFolder',
    method: 'POST',
    params: {
      folderId
    },
  });
};

export const movePedigreeFolder = (folderDto: { folderId: FolderListDto['id'], newFolderId: FolderListDto['id'] }) => {
	return client({
    path: 'api/folders/movePedigreeFolder',
    method: 'POST',
		entity: folderDto,
    params: folderDto
  })
};

export const renamePedigreeFolder = (folderDto: { folderId: FolderListDto['id'], name: string }) => {
	return client({
    path: 'api/folders/renamePedigreeFolder',
    method: 'POST',
    params: folderDto
  })
};

export type ModuleFoldersSetUp = { rootFolderName: string; rootFolderImgUrl: string; rootFolderId?: number | string; };

export type GetModuleFoldersParams = {
  folderTypeId: number;
  systemFolderConfig?: { include?: boolean };
  setUp: ModuleFoldersSetUp;
};

export const getModuleFolders = ({ 
  folderTypeId, 
  setUp,
  systemFolderConfig
}: GetModuleFoldersParams) => {
  return client({
    path: 'api/folders/module/loadFolders',
    params: {
      folderTypeId
    },
    method: 'GET',
  }).then((response: HttpSuccessDto<Array<FolderPrimaryListDto>>) => {
    const createRootFolder = ({ id, name, children, imageURL, system }: Partial<FolderPrimaryListDto>) => {
      return {
        id, 
        name,
        children,
        imageURL,
        system,
        imageURLSelected: imageURL,
      };
    };
    
    const foldersByType = response.entity.reduce<{ custom: Array<FolderPrimaryListDto>; system: Array<FolderPrimaryListDto> }>((acc, folder) => {
      if (folder.system) {
        folder.imageURL = 'gif/system.gif';
        folder.imageURLSelected = 'gif/system.gif';

        acc.system.push(folder);
      } else {
        acc.custom.push(folder);
      }

      return acc;
    }, { custom: [], system: [] });

    const systemRootFolder = createRootFolder({
      id: 0,
      name: 'System Fields',
      children: foldersByType.system,
      imageURL: 'gif/system.gif',
      system: true,
    });
    const customRootFolder = createRootFolder({
      id: setUp.rootFolderId ?? '' as any,
      name: setUp.rootFolderName,
      children: foldersByType.custom,
      imageURL: setUp.rootFolderImgUrl,
      system: false,
    });

    const folders = [customRootFolder];

    const includeSystemRootFolder = systemFolderConfig?.include ?? true;

    if (includeSystemRootFolder) {
      folders.unshift(systemRootFolder);
    }

    return folders;
  });
};

export const getDatasheetFolders = async (): Promise<Array<DatasheetFolderListDto>> => {
  return clientTransformed({
    path: 'api/datasheet/loadDSFolders',
    method: 'GET',
  }).then(
    (response: { entity: Array<DatasheetFolderListDto> }) => {
      return response.entity;
    },
  );
};